import {
  BackendCashSession,
  BackendCashSessionDeclaration,
  BackendCashSessionSummary,
  BackendCashSessionSummaryDocumentTypeAmount,
  BackendCashSessionSummaryGroup,
  BackendCashSessionSummaryItem,
  BackendCashTransaction,
  CashSession,
  CashSessionDeclaration,
  CashSessionSummary,
  CashSessionSummaryDocumentTypeAmount,
  CashSessionSummaryGroup,
  CashSessionSummaryItem,
  CashTransaction,
} from '@/@types/cash-management';

function toNumber(value: number | string | null | undefined) {
  return value === null || value === undefined ? 0 : Number(value);
}

function formatDateTime(value: string | null) {
  if (!value) return null;

  return new Date(value).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function mapBackendCashTransactionToCashTransaction(
  transaction: BackendCashTransaction
): CashTransaction {
  return {
    id: transaction.id,
    number: transaction.number,
    cashSessionId: transaction.cash_session_id,
    cashSession: transaction.cash_session
      ? {
          id: transaction.cash_session.id,
          number: transaction.cash_session.number,
        }
      : undefined,
    type: transaction.type,
    documentTypeId: transaction.document_type_id,
    documentType: transaction.document_type,
    amount: toNumber(transaction.amount),
    description: transaction.description,
    outputId: transaction.output_id,
    createdAt: formatDateTime(transaction.created_at) || '',
  };
}

export function mapBackendDeclarationToDeclaration(
  declaration: BackendCashSessionDeclaration
): CashSessionDeclaration {
  return {
    id: declaration.id,
    documentTypeId: declaration.document_type_id,
    documentType: declaration.document_type,
    expectedAmount: toNumber(declaration.expected_amount),
    declaredAmount: toNumber(declaration.declared_amount),
    discrepancy: toNumber(declaration.discrepancy),
  };
}

export function mapBackendCashSessionToCashSession(
  cashSession: BackendCashSession
): CashSession {
  return {
    id: cashSession.id,
    number: cashSession.number,
    userId: cashSession.user_id,
    user: cashSession.user
      ? {
          id: cashSession.user.id,
          number: cashSession.user.number,
          name: cashSession.user.name,
        }
      : undefined,
    status: cashSession.status,
    openedAt: formatDateTime(cashSession.opened_at),
    reconciliationStartedAt: formatDateTime(
      cashSession.reconciliation_started_at
    ),
    closedAt: formatDateTime(cashSession.closed_at),
    startingCash: toNumber(cashSession.starting_cash),
    expectedCash:
      cashSession.expected_cash === null
        ? null
        : toNumber(cashSession.expected_cash),
    declaredCash:
      cashSession.declared_cash === null
        ? null
        : toNumber(cashSession.declared_cash),
    discrepancy:
      cashSession.discrepancy === null
        ? null
        : toNumber(cashSession.discrepancy),
    createdAt: formatDateTime(cashSession.created_at) || '',
    updatedAt: cashSession.updated_at,
    transactions: cashSession.transactions?.map(
      mapBackendCashTransactionToCashTransaction
    ),
    declarations: cashSession.declarations?.map(
      mapBackendDeclarationToDeclaration
    ),
  };
}

function mapBackendSummaryItemToSummaryItem(
  item: BackendCashSessionSummaryItem
): CashSessionSummaryItem {
  return {
    id: item.id,
    type: item.type,
    direction: item.direction,
    defaultLabel: item.default_label,
    description: item.description,
    amount: toNumber(item.amount),
    createdAt: item.created_at,
    documentType: item.document_type,
    output: item.output
      ? {
          id: item.output.id,
          number: item.output.number,
          operationName: item.output.operation_name,
          customerName: item.output.customer_name,
        }
      : null,
  };
}

function mapBackendSummaryGroupToSummaryGroup(
  group: BackendCashSessionSummaryGroup
): CashSessionSummaryGroup {
  return {
    documentType: group.document_type,
    items: group.items.map(mapBackendSummaryItemToSummaryItem),
    subtotal: toNumber(group.subtotal),
  };
}

function mapBackendDocumentTypeAmountToDocumentTypeAmount(
  summary: BackendCashSessionSummaryDocumentTypeAmount
): CashSessionSummaryDocumentTypeAmount {
  return {
    documentType: summary.document_type,
    amount: toNumber(summary.amount),
  };
}

export function mapBackendSummaryToSummary(
  summary: BackendCashSessionSummary
): CashSessionSummary {
  return {
    cashSession: {
      id: summary.cash_session.id,
      number: summary.cash_session.number,
      status: summary.cash_session.status,
      openedAt: summary.cash_session.opened_at,
      reconciliationStartedAt: summary.cash_session.reconciliation_started_at,
      closedAt: summary.cash_session.closed_at,
      user: summary.cash_session.user,
    },
    groups: {
      credits: summary.groups.credits.map(mapBackendSummaryGroupToSummaryGroup),
      debits: summary.groups.debits.map(mapBackendSummaryGroupToSummaryGroup),
    },
    summaries: {
      credits: summary.summaries.credits.map(
        mapBackendDocumentTypeAmountToDocumentTypeAmount
      ),
      debits: summary.summaries.debits.map(
        mapBackendDocumentTypeAmountToDocumentTypeAmount
      ),
      futureCredits: summary.summaries.future_credits.map(
        mapBackendDocumentTypeAmountToDocumentTypeAmount
      ),
      salesByDocumentType: summary.summaries.sales_by_document_type.map(
        mapBackendDocumentTypeAmountToDocumentTypeAmount
      ),
      cashTotals: {
        documentType: summary.summaries.cash_totals.document_type,
        startingCash: toNumber(summary.summaries.cash_totals.starting_cash),
        cashSales: toNumber(summary.summaries.cash_totals.cash_sales),
        cashPayIns: toNumber(summary.summaries.cash_totals.cash_pay_ins),
        totalCredits: toNumber(summary.summaries.cash_totals.total_credits),
        totalDebits: toNumber(summary.summaries.cash_totals.total_debits),
        finalBalance: toNumber(summary.summaries.cash_totals.final_balance),
      },
    },
  };
}
